/**
 * Block tools: the layout surface of the shared build tools.
 *
 * A model declares structure: a `unit`, and `stack`, `panel`, `row`, `text`, `image`, `button`, `chip`, `facts` and `stage3d` blocks
 * inside it. It never writes a position, a scale, a font size or a container size. Each block records WHAT it is; the sizing that
 * makes it respond to a resize is written once the tree is known ({@link BlockTools.finalize}), because a block's item fields depend
 * on the parent it ends up in. Free 3D (primitives, parts, `move`) belongs inside a `stage3d` and nowhere else.
 *
 * The blocks are general: they nest to any depth and combine freely. Arrangements beyond a flow (arc, orbit, depth stack) are a later
 * addition and attach at the stage's content group, which is the one place free transforms are allowed.
 */
import { hash } from './build-hash';
import type { SpatialObjectDescriptor, SpatialTemplate } from './spatial-types';
import {
    DENSITY, DOMINANT_SIDEBAR, ITEM_MIN, LEAD_WEIGHT, RADIUS, RUNGS, SPACING, SURFACES, THEME_DEFAULTS, TYPE_ROLES, UNIT_KINDS, UNIT_PRESETS, ASPECT_NAMES,
    itemMin, parseAspect, protoSpace, radius, rungOf, rungType, space, stepRung, surfaceFill, surfaceText, themeDeclarations, typeEmM,
    type ItemMinToken, type RadiusToken, type Rung, type SpaceToken, type StyleValues, type Surface, type UnitKind, type UnitPreset,
} from './design-tokens';
import type { StateDefinition } from '../../exports/behaviors';
import { MAX_TEXT_SIZES } from '../../layout/legibility';
import { aspectItemSizing } from './aspect-sizing';
import { createSpaceLift } from './space-lift';
import { compileHtml, type HtmlKit, type HtmlOptions, type Look, type MotionRequest } from './html-blocks';
import { colorDistance, nearestColor, parseColor } from './html-style';
import { SVG_GLB_PREFIX, SVG_RELIEF, type SvgPlan } from './html-svg';
import { bakePose, canopyEasing, cycleOf, opacityCycle, opacityRun, planClip, startPhase, REST } from './css-motion';

/** Press feedback for a button: one definition, shared by `makeButton` and the blocks `button`. */
export const BUTTON_STATES: Record<string, StateDefinition> = {
    ':hover': { values: {}, transform: { scale: [1.12, 1.12, 1.12] }, transition: { duration: 0.15, easing: 'easeOut' } },
    ':active': { values: {}, transform: { scale: [0.93, 0.93, 0.93] }, transition: { duration: 0.08 } },
};

type BlockKind = 'unit' | 'stack' | 'panel' | 'row' | 'split' | 'text' | 'image' | 'button' | 'chip' | 'stage' | 'stageContent' | 'overlay' | 'svg';

interface Block {
    kind: BlockKind;
    id: string;
    dir?: 'X' | 'Y';
    unitKind?: UnitKind;
    width?: number;
    gap: SpaceToken;
    padding: SpaceToken;
    surface?: Surface;
    body?: string;
    radius?: RadiusToken;
    wrap?: boolean;
    minItem?: ItemMinToken;
    rung?: Rung;
    /** Characters of a text, for deciding whether it hugs or fills. */
    chars?: number;
    /** Characters of its longest word: the narrowest it can be set without breaking a word. */
    word?: number;
    textAlign?: 'left' | 'center' | 'right';
    bold?: boolean;
    aspect?: unknown;
    /** The zone that dominates its unit: the largest share where it sits beside others, and the top type rung. */
    lead?: boolean;
    /** A split's sidebar side. */
    side?: 'start' | 'end';
    /** Gap in metres when it is not a space step (a split's template gap). */
    gapM?: number;
    /** Padding in metres when it is not a space step (a dense column's). */
    padM?: number;
    /** A line of texts set in different styles: its texts hug and sit side by side, wrapping when the line is full. */
    inline?: boolean;
    /** A horizontal box whose items are sized by their content (CSS flex items with no grow), not by equal shares. */
    content?: boolean;
    /** An overlay's edges (px against the 1280 reference; undefined is not set) and whether it sits behind its siblings. */
    edges?: { top?: number; right?: number; bottom?: number; left?: number };
    behind?: boolean;
    depthStep?: number;
    /** A button in a column that is as wide as its label, at this side. */
    hugSelf?: 'Start' | 'Center' | 'End';
    /** A dot, swatch or sized box: a fixed size in px against the 1280 reference. `hugH`: with no height, keep hugging the content instead of filling. */
    fixedPx?: { w?: number; h?: number; hugH?: boolean };
    /** An inline svg: the drawn shapes, and its height in em of the text it sits with. */
    svg?: { plan: SvgPlan; em: number };
}

/** A colour scaled toward black by `k`: the emissive lift that keeps a flat colour true under the viewer's light. */
const lift = (hex: string, k: number) => '#' + [1, 3, 5].map(i => Math.round(parseInt(hex.slice(i, i + 2), 16) * k).toString(16).padStart(2, '0')).join('');

/** What the block tools need from the collector that owns the descriptors. */
export interface BlockHost {
    create(template: SpatialTemplate, name: string): string;
    find(id: string): SpatialObjectDescriptor | undefined;
    objects(): readonly SpatialObjectDescriptor[];
    behavior(id: string, type: string, overrides: Record<string, unknown>): void;
    parent(child: string, parent: string): void;
    background(target: string, opts: { color: string; cornerRadius: number; name: string; opacity?: number }): string;
    /** Children parented to `from` go to `to` instead (a stage's handle stands for its content group). */
    redirect(from: string, to: string): void;
    /** A new empty group between `child` and its parent, in the child's place in the parent's order; undefined when the child has no parent block. */
    interpose(child: string, name: string): string | undefined;
    /** The builder's `interaction(source)`, for `html()` to wire buttons and start entrances. */
    interaction?(source: string): ReturnType<HtmlKit['interaction']>;
    /** A build warning. */
    warn?(message: string): void;
    /** The builder's `color(handle, hex)`, for a handle that is not a block (free 3D in a stage). */
    color(target: string, hex: unknown): void;
}

interface Opts { into?: string; name?: string }
interface Entrance { kind: 'rise' | 'slide-left' | 'slide-right'; delay: number }
/** A zone a model can mark as the one that dominates. */
interface ZoneOpts extends Opts { lead?: boolean }
type AlignOpt = 'start' | 'center' | 'end' | 'stretch';
interface ContainerOpts extends ZoneOpts { gap?: SpaceToken; padding?: SpaceToken; align?: AlignOpt; content?: boolean }

/** The widths a unit may be laid out at, metres. Below the smallest preset's 0.4 the fixed 10 mm slabs stop reading as flat panels. */
/** The reference width CSS px are read against. */
const CANVAS = 1280;
const MIN_UNIT_WIDTH_M = 0.3;
const MAX_UNIT_WIDTH_M = 10;

/**
 * A block's padding, on the four sides in the plane only. A uniform number also pads the depth, and a block's children sit at the front
 * of its depth, so that padding would push them back into, or behind, the block's own face.
 */
const padObject = (p: number, v: number = p) => p > 0 || v > 0 ? { left: p, right: p, top: v, bottom: v, front: 0, back: 0 } : 0;

const ALIGN = { start: 'Start', center: 'Center', end: 'End', stretch: 'Stretch' } as const;

function pick<T extends string>(what: string, value: unknown, allowed: readonly string[], fallback?: T): T {
    if (value === undefined && fallback !== undefined) return fallback;
    if (typeof value === 'string' && allowed.includes(value)) return value as T;
    throw new Error(`${what}: ${JSON.stringify(value)} is not one of ${allowed.join(', ')}.`);
}

export interface BlockTools {
    /** The tools, by the names a model calls them. */
    api: Record<string, unknown>;
    /** Check the tree and write each block's sizing. Returns errors with a fix hint; writes nothing when there are any. */
    finalize(): string[];
    /** What finalize changed to keep the composition's rules (hierarchy, density), in words, for the build's warnings. */
    notes(): string[];
}

export interface BlockToolOptions {
    /** The picked Style's values: its typography sets the type ramp and its colours and fonts stand in for the defaults. */
    style?: StyleValues;
    /** Hosted font families the deployment declares, beyond the bundled ones: a page's `font-family` may name them. */
    fonts?: readonly string[];
}

export function createBlockTools(host: BlockHost, options: BlockToolOptions = {}): BlockTools {
    const style = options.style;
    const blocks = new Map<string, Block>();
    const order: Block[] = [];
    const entrances = new Map<string, Entrance>();
    const motions = new Map<string, MotionRequest>();
    /** What the page's CSS asks of a block's look (exact colours, type options), applied in `finalize`. */
    const looks = new Map<string, Look>();
    const weights = new Map<string, number>();
    /** html() page with px sizes: one px-to-metres scale for type, gaps and padding. */
    let faithful = false;

    // Exact colours from a page are kept as colour properties on the unit, each tied to the Style role it is nearest to, so a Style
    // switch can re-skin by role: `htmlColorRoles` on the unit lists them. A colour equal to a role's own value binds that role directly.
    const themeValue = (name: string): string => {
        const given = style?.[name];
        return (typeof given === 'string' && /^#[0-9a-f]{6}$/i.test(given) ? given : THEME_DEFAULTS[name]).toUpperCase();
    };
    const FILL_NAMES = SURFACES.map(x => `${x}Color`);
    const INK_NAMES = Object.keys(THEME_DEFAULTS).filter(n => n.endsWith('Color'));
    interface ColorOverride { name: string; value: string; role: string; kind: 'fill' | 'ink' }
    const overrides = new Map<string, ColorOverride[]>();
    function colorRef(unitId: string, hex: string, kind: 'fill' | 'ink'): string {
        const value = hex.toUpperCase();
        const candidates = Object.fromEntries((kind === 'fill' ? FILL_NAMES : INK_NAMES).map(n => [n, themeValue(n)]));
        const role = nearestColor(value, candidates);
        if (colorDistance(value, candidates[role]) < 1) return `{${role}}`;
        const list = overrides.get(unitId) ?? [];
        overrides.set(unitId, list);
        let have = list.find(o => o.kind === kind && o.value === value);
        if (!have) {
            have = { name: `page${kind === 'fill' ? 'Fill' : 'Ink'}${list.filter(o => o.kind === kind).length + 1}`, value, role, kind };
            list.push(have);
        }
        return `{${have.name}}`;
    }

    function register(block: Block): Block {
        blocks.set(block.id, block);
        order.push(block);
        return block;
    }
    function place(id: string, opts?: Opts): void {
        if (opts?.into !== undefined) host.parent(id, opts.into);
    }
    const nameOf = (id: string) => host.find(id)?.name ?? id;

    function unit(kind: unknown, preset: unknown, opts?: Opts & { width?: number }): string {
        const k = pick<UnitKind>('unit(kind, preset): kind', kind, UNIT_KINDS);
        const p = pick<UnitPreset>('unit(kind, preset): preset', preset, Object.keys(UNIT_PRESETS));
        const { stageAspect } = UNIT_PRESETS[p];
        // The width of the slot this unit stands in, when the host knows it: the type ramp, gaps and padding are all shares of the unit
        // width, so a unit laid out at its slot's real size keeps the ramp, where a finished unit resized afterwards keeps its old type.
        const asked = opts?.width;
        if (asked !== undefined && !(typeof asked === 'number' && Number.isFinite(asked) && asked >= MIN_UNIT_WIDTH_M && asked <= MAX_UNIT_WIDTH_M)) {
            throw new Error(`unit(kind, preset, { width }): ${JSON.stringify(asked)} is not a width in metres from ${MIN_UNIT_WIDTH_M} to ${MAX_UNIT_WIDTH_M}.`);
        }
        const width = asked ?? UNIT_PRESETS[p].width;
        const id = host.create('empty', opts?.name ?? 'Unit');
        const block = register({
            kind: 'unit', id, unitKind: k, width, dir: 'Y', gap: 's2', padding: k === 'stage' ? 's0' : 's3',
            surface: 'background', radius: 'r1',
        });
        host.behavior(id, 'Layout', {
            direction: 'Y', gap: space(block.gap, width), padding: padObject(space(block.padding, width)),
            alignMain: 'Start', alignCross: 'Stretch', alignCrossB: 'End',
            width, widthMode: 'Fixed',
            ...(k === 'stage' ? { height: Math.round(width / stageAspect * 1e6) / 1e6, heightMode: 'Fixed' } : { heightMode: 'Hug' }),
            depth: 0.01, depthMode: 'Fixed',
        });
        host.behavior(id, 'Properties', { properties: themeDeclarations(style) });
        if (k !== 'stage') {
            block.body = host.background(id, { color: surfaceFill('background'), cornerRadius: radius('r1', width), name: 'Unit Body' });
        }
        return id;
    }

    function container(kind: 'stack' | 'panel' | 'row' | 'split', dir: 'X' | 'Y', defaults: ContainerOpts, opts: ContainerOpts | undefined, extra: Partial<Block> = {}): string {
        const o = { ...defaults, ...opts };
        const gap = pick<SpaceToken>(`${kind}: gap`, o.gap, Object.keys(SPACING));
        const padding = pick<SpaceToken>(`${kind}: padding`, o.padding, Object.keys(SPACING));
        const align = pick<AlignOpt>(`${kind}: align`, o.align, Object.keys(ALIGN));
        const id = host.create('empty', o.name ?? kind[0].toUpperCase() + kind.slice(1));
        register({ kind, id, dir, gap, padding, ...(o.lead ? { lead: true } : {}), ...(o.content && dir === 'X' ? { content: true } : {}), ...extra });
        host.behavior(id, 'Layout', {
            direction: dir, alignMain: 'Start', alignCross: ALIGN[align], alignCrossB: 'End', depth: 0.01, depthMode: 'Fixed',
        });
        place(id, o);
        return id;
    }

    function stack(dir: unknown, opts?: ContainerOpts & { inline?: boolean }): string {
        const d = pick<'x' | 'y'>('stack(dir, opts): dir', dir, ['x', 'y']);
        return container('stack', d === 'x' ? 'X' : 'Y', { gap: 's2', padding: 's0', align: 'stretch' }, opts, opts?.inline ? { inline: true } : {});
    }

    function panel(surface?: unknown, opts?: ContainerOpts & { dir?: 'x' | 'y'; radius?: RadiusToken }): string {
        const s = pick<Surface>('panel(surface, opts): surface', surface, SURFACES, 'card');
        const r = pick<RadiusToken>('panel: radius', opts?.radius, Object.keys(RADIUS), 'r2');
        const id = container('panel', opts?.dir === 'x' ? 'X' : 'Y', { gap: 's2', padding: 's3', align: 'stretch' }, opts, { surface: s, radius: r });
        blocks.get(id)!.body = host.background(id, { color: surfaceFill(s), cornerRadius: 0, name: `${nameOf(id)} Body` });
        return id;
    }

    function row(opts?: ContainerOpts & { wrap?: boolean; minItem?: ItemMinToken }): string {
        const wrap = opts?.wrap ?? true;
        const minItem = pick<ItemMinToken>('row: minItem', opts?.minItem, Object.keys(ITEM_MIN), 's');
        return container('row', 'X', { gap: 's2', padding: 's0', align: 'stretch' }, opts, { wrap, minItem });
    }

    /**
     * Two zones side by side, one dominant and one a sidebar, in the prototype's Dominant + Sidebar proportions (main 800, sidebar 360,
     * gap 120 on its 1280 canvas), which hold as the unit is resized. The child marked `lead` is the main zone; without one, the first child
     * is, or the second with `side: 'start'`. A split's main zone is the unit's lead unless another zone is marked.
     */
    function split(opts?: Omit<ContainerOpts, 'lead'> & { side?: 'start' | 'end' }): string {
        const side = pick<'start' | 'end'>('split: side', opts?.side, ['start', 'end'], 'end');
        return container('split', 'X', { gap: 's0', padding: 's0', align: 'stretch' }, { ...opts, lead: undefined }, { side, wrap: false });
    }

    /**
     * A layer over (or behind) its parent's content, taken out of the flow: it fills the parent and places what it holds by the edges the
     * CSS set (`top`, `left`, ... in px of the 1280 reference), so an absolutely placed box sits where its edges say whatever the parent's size.
     */
    function overlay(opts: { into: string; name?: string; edges?: { top?: number; right?: number; bottom?: number; left?: number }; behind?: boolean; depthStep?: number }): string {
        const id = host.create('empty', opts.name ?? 'Overlay');
        register({ kind: 'overlay', id, dir: 'Y', gap: 's0', padding: 's0', edges: opts.edges ?? {}, ...(opts.behind ? { behind: true } : {}), depthStep: opts.depthStep ?? 0 });
        place(id, { into: opts.into });
        host.behavior(id, 'LayoutItem', { positionMode: 'Absolute', widthMode: 'Fill', heightMode: 'Fill', depthMode: 'Fixed', depth: 0.01 });
        host.find(id)!.position = [0, 0, opts.behind ? -0.001 : 0.012 + 0.003 * (opts.depthStep ?? 0)];
        return id;
    }

    function text(role: unknown, content: unknown, opts?: Opts & { align?: 'left' | 'center' | 'right'; bold?: boolean; look?: Look }): string {
        const r = rungOf(role);
        if (r === null) throw new Error(`text(role, content): role ${JSON.stringify(role)} is not one of ${[...Object.keys(TYPE_ROLES), ...RUNGS].join(', ')}.`);
        if (typeof content !== 'string' && typeof content !== 'number') throw new Error('text(role, content): content must be a string.');
        const str = String(content);
        const id = host.create('empty', opts?.name ?? `${String(role)} ${str.slice(0, 24)}`);
        register({ kind: 'text', id, gap: 's0', padding: 's0', rung: r, chars: str.length, word: str.split(/\s+/).reduce((m, x) => Math.max(m, x.length), 0), textAlign: opts?.align ?? 'left', bold: opts?.bold });
        host.behavior(id, 'Text', { text: str });
        if (opts?.look && Object.keys(opts.look).length) looks.set(id, opts.look);
        place(id, opts);
        return id;
    }

    function image(prompt: unknown, aspect?: unknown, opts?: ZoneOpts): string {
        if (typeof prompt !== 'string' || !prompt) throw new Error('image(prompt, aspect): prompt must be a generation prompt (string).');
        const id = host.create('empty', opts?.name ?? `Image ${prompt.slice(0, 24)}`);
        register({ kind: 'image', id, gap: 's0', padding: 's0', aspect, ...(opts?.lead ? { lead: true } : {}) });
        host.find(id)!.imagePrompt = prompt;
        host.behavior(id, 'Image', {});
        place(id, opts);
        return id;
    }

    /**
     * An inline svg: its shapes are baked to a GLB (one mesh, a primitive per colour) the block's Model plays, and `finalize` sizes it as
     * `em` of the text around it. Its colours bind to Style roles like text colour does.
     */
    function svg(plan: SvgPlan, opts: Opts & { em: number; rung: Rung; look?: Look }): string {
        // The file holds the shapes and the box's bounds, so both name it.
        const src = `${SVG_GLB_PREFIX}${hash(JSON.stringify([plan.width / plan.height, plan.pieces]))}.glb`;
        const id = host.create('empty', opts.name ?? 'Icon');
        register({ kind: 'svg', id, gap: 's0', padding: 's0', rung: opts.rung, svg: { plan, em: opts.em } });
        const desc = host.find(id)!;
        desc.svg = { src, aspect: plan.width / plan.height, pieces: plan.pieces };
        if (opts.look && Object.keys(opts.look).length) looks.set(id, opts.look);
        place(id, opts);
        return id;
    }

    /** A label on a surface: a button and a chip differ in role and default surface only. */
    function labelled(kind: 'button' | 'chip', label: unknown, opts?: Opts & { surface?: Surface; bare?: boolean; leading?: (id: string) => void; iconOnly?: boolean }): string {
        if (typeof label !== 'string' || !label) throw new Error(`${kind}(label): label must be a string.`);
        const surface = pick<Surface>(`${kind}: surface`, opts?.surface, SURFACES, kind === 'button' ? 'primary' : 'muted');
        const id = host.create('empty', opts?.name ?? `${kind} ${label.slice(0, 24)}`);
        register({ kind, id, dir: 'X', gap: 's0', padding: kind === 'button' ? 's3' : 's2', surface, radius: 'r3' });
        host.behavior(id, 'Layout', {
            direction: 'X', alignMain: 'Center', alignCross: 'Center', alignCrossB: 'End', depth: 0.01, depthMode: 'Fixed',
        });
        if (!opts?.bare) blocks.get(id)!.body = host.background(id, { color: surfaceFill(surface), cornerRadius: 0, name: `${nameOf(id)} Body` });
        if (kind === 'button') host.behavior(id, 'Properties', { properties: [], states: BUTTON_STATES });
        opts?.leading?.(id);
        if (!opts?.iconOnly) text(kind === 'button' ? 'body' : 'caption', label, { into: id, bold: kind === 'button', align: 'center', name: `${nameOf(id)} Label` });
        place(id, opts);
        return id;
    }

    /**
     * An image with its caption: a recipe on `stack`, `image` and `text`. The caption sits under the image, starts at its edge and wraps
     * within its width, in the caption rung; the pair moves and resizes as one.
     */
    function figure(prompt: unknown, aspect: unknown, caption: unknown, opts?: ZoneOpts): string {
        if (typeof caption !== 'string' || !caption) throw new Error('figure(prompt, aspect, caption): caption must be a string.');
        const id = stack('y', { gap: 's1', into: opts?.into, name: opts?.name ?? 'Figure', lead: opts?.lead });
        image(prompt, aspect, { into: id, name: `${nameOf(id)} Image` });
        text('caption', caption, { into: id, name: `${nameOf(id)} Caption` });
        return id;
    }

    /** A row of label/value pairs; a recipe on `row` and `text`, so it wraps and shares width like any row. */
    function facts(items: unknown, opts?: ContainerOpts & { minItem?: ItemMinToken }): string {
        if (!Array.isArray(items) || items.length === 0) throw new Error('facts(items): items must be a non-empty array of { label, value }.');
        const id = row({ ...opts, name: opts?.name ?? 'Facts' });
        items.forEach((item: { label?: unknown; value?: unknown }, i: number) => {
            const readable = (v: unknown) => typeof v === 'string' || typeof v === 'number';
            if (item === null || typeof item !== 'object' || !readable(item.label) || !readable(item.value)) {
                throw new Error(`facts(items): items[${i}] must be { label, value }.`);
            }
            const cell = stack('y', { gap: 's1', into: id, name: `Fact ${i + 1}` });
            text('caption', String(item.label), { into: cell, name: `Fact ${i + 1} Label` });
            text('body', String(item.value), { into: cell, bold: true, name: `Fact ${i + 1} Value` });
        });
        return id;
    }

    /**
     * A box of a declared shape (default 4:3) that fits free 3D inside it. The handle stands for the content group inside: parent 3D to
     * the handle and place it freely; the stage scales it to fit as one thing and never re-lays what is in it.
     */
    function stage3d(name?: unknown, aspect?: unknown, opts?: ZoneOpts): string {
        if (name !== null && typeof name === 'object') [name, aspect, opts] = [undefined, undefined, name];
        if (aspect !== null && typeof aspect === 'object') [aspect, opts] = [undefined, aspect];
        const id = host.create('empty', typeof name === 'string' && name ? name : 'Stage');
        register({ kind: 'stage', id, dir: 'Y', gap: 's0', padding: 's0', aspect: aspect ?? '4:3', ...(opts?.lead ? { lead: true } : {}) });
        host.behavior(id, 'Layout', {
            // The stage is a plane as thin as the blocks around it, and its 3D stands on that plane's back and rises toward the viewer.
            // A Hug depth with the content front-aligned (as every other block is) sank deep 3D through the panel it sat in.
            direction: 'Y', padding: 0, gap: 0, alignMain: 'Start', alignCross: 'Stretch', alignCrossB: 'Start',
            depth: 0.01, depthMode: 'Fixed',
        });
        host.behavior(id, 'ContentSlot', { fit: 'contain' });
        const content = host.create('empty', `${nameOf(id)} Content`);
        register({ kind: 'stageContent', id: content, gap: 's0', padding: 's0' });
        host.parent(content, id);
        host.behavior(content, 'LayoutItem', { widthMode: 'Hug', heightMode: 'Hug', depthMode: 'Hug', measureGeometry: true });
        host.redirect(id, content);
        place(id, opts);
        return id;
    }

    const ancestors = (id: string): Block[] => {
        const out: Block[] = [];
        for (let at = host.find(id)?.parentId; at; at = host.find(at)?.parentId) {
            const b = blocks.get(at);
            if (b) out.push(b);
        }
        return out;
    };
    const insideStage = (id: string) => ancestors(id).some(b => b.kind === 'stageContent');

    /** `color()` calls the blocks refused, reported with finalize's errors. */
    const refused: string[] = [];
    /**
     * `color(handle, hex)`: a block draws its colour on its surface's body, as a page's CSS fill does. A block with no surface (a stack,
     * a text) has nothing to colour; a colour on its Layout object draws nothing and moves the body it holds.
     */
    function color(handle: string, hex: unknown): void {
        const id = host.find(handle)?.id;
        const b = id === undefined ? undefined : blocks.get(id);
        if (!b) { host.color(handle, hex); return; }
        const fill = typeof hex === 'string' ? parseColor(hex) : null;
        if (!b.body || !fill) {
            const surfaces = SURFACES.map(x => `'${x}'`).join(' | ');
            refused.push(`color("${nameOf(b.id)}", ${JSON.stringify(hex)}): ` + (fill
                ? `a ${b.kind} has no surface of its own to colour. Fix: colour the panel, button or chip it sits in, or give it a surface: panel(${surfaces}, …), or button/chip(label, { surface: ${surfaces} }).`
                : 'that is not a colour. Fix: pass a CSS colour, "#RRGGBB" or rgb(); an alpha ("#RRGGBBAA", rgba()) makes the surface see-through.'));
            return;
        }
        looks.set(b.id, { ...looks.get(b.id), fill });
    }

    const notesOut: string[] = [];

    function finalize(): string[] {
        const errors: string[] = [...refused];
        const roots = order.filter(b => b.kind === 'unit');
        const fix = (what: string, hint: string) => errors.push(`${what} Fix: ${hint}`);
        if (roots.length > 1) {
            fix(`${roots.length} units were made (${roots.map(b => `"${nameOf(b.id)}"`).join(', ')}); a build is exactly one unit.`,
                'call unit() once, and put the rest inside it: a row() of stage3d() or panel() blocks makes a gallery.');
        }
        const rootOf = new Map<string, Block | undefined>();
        for (const b of order) {
            const up = [b, ...ancestors(b.id)];
            rootOf.set(b.id, up.find(x => x.kind === 'unit'));
        }
        for (const b of order) {
            const desc = host.find(b.id)!;
            const where = `"${desc.name}" (${b.kind})`;
            if (b.kind !== 'unit' && !rootOf.get(b.id)) {
                fix(`${where} is not inside a unit.`, 'create the page with unit(kind, preset) first and put this in it with parent() or { into }.');
                continue;
            }
            // A block sizes from its parent block, so one under a plain group has nothing to size from.
            const parentId = desc.parentId;
            if (b.kind !== 'unit' && parentId && !blocks.has(parentId)) {
                if (insideStage(b.id)) fix(`${where} is inside a stage, which holds free 3D only.`, 'put layout blocks in the unit, beside the stage, in a stack or row.');
                else fix(`${where} is inside "${host.find(parentId)?.name ?? parentId}", which is not a layout block.`, 'parent layout blocks to unit(), stack(), panel(), row(), split(), button() or chip().');
                continue;
            }
            const up = host.find(b.id)?.parentId ? blocks.get(host.find(b.id)!.parentId!) : undefined;
            if (up && (up.kind === 'text' || up.kind === 'image')) fix(`${where} is inside a ${up.kind}, which holds no children.`, 'put it in a stack, panel or row instead.');
            if ((b.kind === 'image' || b.kind === 'stage') && parseAspect(b.aspect) === null) {
                fix(`${where} has no usable aspect (${JSON.stringify(b.aspect)}).`,
                    `${b.kind === 'image' ? 'image(prompt, aspect)' : 'stage3d(name, aspect)'} takes a ratio, "w:h", or ${Object.keys(ASPECT_NAMES).join(', ')}.`);
            }
        }
        for (const desc of host.objects()) {
            const b = blocks.get(desc.id);
            const parent = desc.parentId ? blocks.get(desc.parentId) : undefined;
            const absolute = desc.behaviors?.some(x => x.type === 'LayoutItem' && x.overrides?.positionMode === 'Absolute');
            const flowed = !!parent && parent.kind !== 'stageContent' && !absolute;
            if (!b && flowed) {
                fix(`"${desc.name}" is a free object inside a layout flow.`, 'free 3D belongs in a stage: const s = stage3d("Name"), then parent it to s.');
            } else if (b && parent?.kind === 'stageContent' && b.kind !== 'stageContent') {
                fix(`"${desc.name}" (${b.kind}) is inside a stage, which holds free 3D only.`, 'put layout blocks in the unit, beside the stage, in a stack or row.');
            } else if (flowed && (desc.position || desc.scale)) {
                fix(`"${desc.name}" is in a layout flow and was moved or scaled; the layout owns its place and size.`,
                    'drop move()/scale(); change the block (role, gap, padding, minItem) or the parent instead.');
            }
            if (!b && !insideStage(desc.id) && desc.behaviors?.some(x => x.type === 'Text')) {
                fix(`"${desc.name}" has Text outside a layout flow.`, 'use text(role, content) inside a stack, panel or row (a callout belongs inside a stage3d).');
            }
            if (!b && !desc.parentId) continue;
            for (const x of desc.behaviors ?? []) {
                if ((x.type !== 'Layout' && x.type !== 'LayoutItem') || b?.kind === 'unit' || x.overrides?.positionMode === 'Absolute') continue;
                for (const axis of ['width', 'height'] as const) {
                    if (x.overrides?.[`${axis}Mode`] === 'Fixed' || (x.overrides?.[axis] !== undefined && x.overrides[`${axis}Mode`] === undefined)) {
                        fix(`"${desc.name}" has a Fixed ${axis}; only the unit is sized.`, `leave ${axis} to the layout (Fill or Hug) and size through the unit preset, role and space tokens.`);
                    }
                }
            }
        }
        const childrenOf = (id: string) => order.filter(x => host.find(x.id)?.parentId === id);
        for (const b of order.filter(x => x.kind === 'split')) {
            const n = childrenOf(b.id).length;
            if (n !== 2) fix(`"${nameOf(b.id)}" (split) holds ${n} blocks; a split is exactly a main zone and a sidebar.`, 'put two blocks in it (a panel or stack each), and the rest inside those.');
        }
        const leads = order.filter(x => x.lead);
        if (leads.length > 1) {
            fix(`${leads.length} zones are marked lead (${leads.map(x => `"${nameOf(x.id)}"`).join(', ')}); a composition has one dominant zone.`,
                'keep { lead: true } on the one zone that should dominate and drop it from the others.');
        }
        if (errors.length) return errors;

        if (faithful) for (const r of order.filter(x => x.kind === 'unit')) host.find(r.id)!.faithfulScale = true;
        // A page's own padding (a rim's border width, a button's padding) in metres.
        for (const b of order) {
            const l = looks.get(b.id);
            const w = rootOf.get(b.id)?.width;
            if (!l || w === undefined) continue;
            if (l.padding !== undefined) b.padM = Math.round(l.padding * w * 1e6) / 1e6;
            if (faithful && l.padPx !== undefined) b.padM = Math.round(l.padPx / CANVAS * w * 1e6) / 1e6;
            if (faithful && l.gapPx !== undefined) b.gapM = Math.round(l.gapPx / CANVAS * w * 1e6) / 1e6;
        }
        /** A wrapping row's narrowest item: the page's own px minimum, else the token. */
        const itemMinOf = (p: Block, unitW: number): number => {
            const px = faithful ? looks.get(p.id)?.minItemPx : undefined;
            return px !== undefined ? Math.round(px / CANVAS * unitW * 1e6) / 1e6 : itemMin(p.minItem!, unitW);
        };
        // The page decides its own margin: the unit takes the body's padding, or none.
        if (faithful) {
            for (const u of order.filter(x => x.kind === 'unit' && x.unitKind !== 'stage')) {
                if (looks.get(u.id)?.padPx === undefined) u.padM = 0;
                host.behavior(u.id, 'Layout', { padding: padObject(u.padM ?? 0) });
            }
        }
        const fontCm = (b: Block, unitW: number): number => {
            const parent = blocks.get(host.find(b.id)?.parentId ?? '');
            const px = ({ ...(parent && (parent.kind === 'button' || parent.kind === 'chip') ? looks.get(parent.id) : undefined), ...looks.get(b.id) }).fontPx;
            return faithful && px !== undefined ? Math.round(typeEmM(px, unitW, 1) * 100 * 1e4) / 1e4 : rungType(b.rung!, unitW, style).fontSize;
        };
        /** How wide `chars` characters of a text are set, estimated at 0.6 em a character. */
        const textWidth = (t: Block, chars: number, unitW: number): number => chars * fontCm(t, unitW) / 100 * 0.6;

        // Density: a column of 5+ or 7+ items steps its type and spacing down, per the prototype's rule. Nested dense columns step once.
        const denseBy = new Map<Block, number>();
        for (const b of order) {
            if (faithful || (b.kind !== 'stack' && b.kind !== 'panel') || b.dir !== 'Y') continue;
            const n = childrenOf(b.id).length;
            const step = DENSITY.find(d => n >= d.items);
            if (!step) continue;
            const unitW = rootOf.get(b.id)!.width!;
            b.gapM = Math.min(space(b.gap, unitW), protoSpace(step.gapDu, unitW));
            if (space(b.padding, unitW) > 0) b.padM = Math.min(space(b.padding, unitW), protoSpace(step.paddingDu, unitW));
            denseBy.set(b, step.rungsDown);
            notesOut.push(`"${nameOf(b.id)}" holds ${n} items: its type steps down ${step.rungsDown} rung${step.rungsDown > 1 ? 's' : ''} and its spacing tightens.`);
        }
        if (denseBy.size) {
            for (const t of order.filter(x => x.kind === 'text')) {
                const by = Math.max(0, ...ancestors(t.id).map(a => denseBy.get(a) ?? 0));
                if (by) t.rung = stepRung(t.rung!, by);
            }
        }

        // Hierarchy: one lead zone, declared or a split's main. It holds the top type rung; text outside it that reaches that rung steps below it.
        const mainOf = (sp: Block): Block => {
            const [first, second] = childrenOf(sp.id);
            return [first, second].find(x => x.lead) ?? (sp.side === 'start' ? second : first);
        };
        const lead = leads[0] ?? (() => { const sp = order.find(x => x.kind === 'split'); return sp && mainOf(sp); })();
        if (lead && !faithful) {
            const inLead = (x: Block) => x === lead || ancestors(x.id).includes(lead);
            const texts = order.filter(x => x.kind === 'text');
            // A caption is no title: a lead of media and its caption sets no type to lead with, so nothing else steps down for it.
            const top = Math.max(-1, ...texts.filter(x => inLead(x) && x.rung !== 'caption').map(x => RUNGS.indexOf(x.rung!)));
            for (const t of texts) {
                if (top < 0 || inLead(t) || RUNGS.indexOf(t.rung!) < top) continue;
                const to = stepRung(RUNGS[top], 1);
                notesOut.push(`"${nameOf(t.id)}": ${t.rung} matched or passed the lead zone's top rung (${RUNGS[top]}); set to ${to} so "${nameOf(lead.id)}" leads.`);
                t.rung = to;
            }
        } else if (order.filter(x => x.kind === 'panel' || x.kind === 'image' || x.kind === 'stage').length >= 2) {
            notesOut.push('No zone leads this composition: mark the one that should dominate with { lead: true }, or arrange it with split().');
        }

        // Type sizes: each unit keeps to the quality check's limit by snapping the rungs it uses together. The largest rung is never merged
        // away; the two closest of the rest become the more used of the pair (the larger on a tie).
        for (const root of order.filter(x => x.kind === 'unit' && !faithful)) {
            const texts = order.filter(x => x.kind === 'text' && rootOf.get(x.id) === root);
            const count = new Map<Rung, number>();
            for (const t of texts) count.set(t.rung!, (count.get(t.rung!) ?? 0) + 1);
            const used = () => [...count.keys()].sort((a, b) => RUNGS.indexOf(b) - RUNGS.indexOf(a));
            while (used().length > MAX_TEXT_SIZES) {
                const r = used();
                let best = -1;
                for (let i = 1; i < r.length - 1; i++) {
                    if (best < 0 || RUNGS.indexOf(r[i]) - RUNGS.indexOf(r[i + 1]) < RUNGS.indexOf(r[best]) - RUNGS.indexOf(r[best + 1])) best = i;
                }
                if (best < 0) best = r.length - 2;
                const [a, b] = [r[best], r[best + 1]];
                const [from, to] = (count.get(b)! > count.get(a)!) ? [a, b] : [b, a];
                for (const t of texts.filter(x => x.rung === from)) {
                    notesOut.push(`"${nameOf(t.id)}": ${from} set to ${to}: a composition keeps to ${MAX_TEXT_SIZES} type sizes.`);
                    t.rung = to;
                }
                count.set(to, count.get(to)! + count.get(from)!);
                count.delete(from);
            }
        }

        // Sizing. `inner(b)` is the width a block's children can use, from the unit's width down.
        const kids = new Map<string, Block[]>();
        const overlaysOf = new Map<string, Block[]>();
        for (const b of order) {
            const p = host.find(b.id)?.parentId;
            if (p && b.kind !== 'overlay') (kids.get(p) ?? kids.set(p, []).get(p)!).push(b);
            else if (p) (overlaysOf.get(p) ?? overlaysOf.set(p, []).get(p)!).push(b);
        }
        // Boxes nest faces inside faces: each block is deep enough for the faces below it, 20 mm per level, so a child's face (aligned to
        // the front) stands clear of its parent's. A stage is a level too: its 3D stands on its back plane, which clears the face it is on.
        const levelMemo = new Map<string, number>();
        function bodyLevels(b: Block): number {
            const have = levelMemo.get(b.id);
            if (have !== undefined) return have;
            const n = (kids.get(b.id) ?? []).reduce((m, c) => Math.max(m, (c.body || c.kind === 'stage' ? 1 : 0) + bodyLevels(c)), 0);
            levelMemo.set(b.id, n);
            return n;
        }
        const depthFor = (b: Block) => Math.round((0.01 + 0.02 * bodyLevels(b)) * 1e6) / 1e6;
        for (const u of order.filter(x => x.kind === 'unit' && bodyLevels(x) > 0)) host.behavior(u.id, 'Layout', { depth: depthFor(u) });
        const widthOf = new Map<string, number>();
        // Gaps and padding are shares of the width, lifted with the type floor in a narrow unit (space-lift.ts).
        const spacing = createSpaceLift<Block>({
            units: order.filter(x => x.kind === 'unit'),
            unitOf: b => rootOf.get(b.id)!,
            items: b => kids.get(b.id) ?? [],
            ancestors: b => ancestors(b.id),
            boxesOf: u => order.filter(b => rootOf.get(b.id) === u && ['unit', 'stack', 'panel', 'row', 'split'].includes(b.kind) && !hugged(b)),
            fixedPad: b => b.kind === 'button' || b.kind === 'chip' || (b.kind === 'panel' && looks.get(b.id)?.padding !== undefined),
            gapBase: (b, unitW) => b.gapM ?? (b.kind === 'split' && b.gap === 's0' ? DOMINANT_SIDEBAR.gap / DOMINANT_SIDEBAR.canvas * unitW : space(b.gap, unitW)),
            padBase: (b, unitW) => b.padM ?? space(b.padding, unitW),
            widthOf: b => widthFor(b),
            innerOf: b => innerOf(b),
            resetWidths: () => widthOf.clear(),
            wordWidth: (t, unitW) => textWidth(t, t.word ?? 0, unitW),
            svgWidth: b => svgSize(b).w,
            itemMin: (row, unitW) => itemMinOf(row, unitW),
            hugWidths: (b, unitW) => hugWidths(b, unitW),
        });
        const { gapOf, crossGapOf, padOf, padDownOf } = spacing;
        /** How a block grows against its siblings in a row: a lead zone by the template's main-to-sidebar, a split by its two shares. */
        const weightOf = (b: Block, p: Block): number => p.kind === 'split'
            ? (mainOf(p) === b ? DOMINANT_SIDEBAR.main : DOMINANT_SIDEBAR.sidebar) / DOMINANT_SIDEBAR.sidebar
            : b.kind === 'svg' ? 0 : weights.has(b.id) ? weights.get(b.id)! : b.kind === 'text' && hugsBesideSiblings(b, p) ? 0 : b === lead ? LEAD_WEIGHT : 1;
        /**
         * In a horizontal panel or stack a short text (a course number, a price) hugs and what sits beside it takes the rest; two equal shares
         * would give a number half the row. Rows keep their shares: their texts are the left and right ends of one line.
         */
        const SHORT_TEXT = 8;
        const hugsBesideSiblings = (t: Block, p: Block): boolean => {
            if (p.dir !== 'X' || (p.kind !== 'panel' && p.kind !== 'stack') || (t.chars ?? 0) > SHORT_TEXT) return false;
            return (kids.get(p.id) ?? []).some(x => x !== t && (x.kind !== 'text' || (x.chars ?? 0) > 2 * (t.chars ?? 0) && (x.chars ?? 0) > SHORT_TEXT));
        };
        /** An svg's size in metres: its height in em of the text beside it (a button's label, else the text it was set with), within its parent. */
        const svgSize = (b: Block): { w: number; h: number } => {
            const unitW = rootOf.get(b.id)!.width!;
            const p = blocks.get(host.find(b.id)?.parentId ?? '');
            // In a button or chip the icon matches the label; elsewhere it keeps the size of the text it was set with.
            const label = (p?.kind === 'button' || p?.kind === 'chip') ? (kids.get(p.id) ?? []).find(x => x.kind === 'text') : undefined;
            const aspect = b.svg!.plan.width / b.svg!.plan.height;
            // A page kept to its own px sizes its svg in px too (in em of that text); otherwise in em of the rung it sits on.
            let h = label ? b.svg!.em * fontCm(label, unitW) / 100
                // The text it was set with, held to the floor as the page's text is.
                : faithful ? b.svg!.em * typeEmM(b.svg!.plan.height / b.svg!.em, unitW, 1)
                    : b.svg!.em * rungType(b.rung!, unitW, style).fontSize / 100;
            const room = p ? innerOf(p) : Infinity;
            if (h * aspect > room && room > 0) h = room / aspect;
            return { w: h * aspect, h };
        };
        const innerOf = (b: Block): number => {
            const w = widthOf.get(b.id) ?? widthFor(b);
            return Math.max(0, w - 2 * padOf(b, rootOf.get(b.id)!.width!));
        };
        function widthFor(b: Block): number {
            const unitW = rootOf.get(b.id)!.width!;
            if (b.kind === 'unit') return b.width!;
            const p = blocks.get(host.find(b.id)!.parentId!)!;
            const pInner = innerOf(p);
            let w = pInner;
            if (b.kind === 'overlay') { widthOf.set(b.id, w); return w; }
            if (p.dir === 'X') {
                const siblings = kids.get(p.id) ?? [];
                const n = siblings.length;
                const gap = gapOf(p, unitW);
                const perLine = p.wrap ? Math.max(1, Math.min(n, Math.floor((pInner + gap) / (itemMinOf(p, unitW) + gap)))) : n;
                const line = siblings.slice(0, perLine);
                const basis = p.wrap ? itemMinOf(p, unitW) : 0;
                // A short text that hugs takes its own width, not a share: what sits beside it gets the rest.
                const hugged = line.reduce((sum, x) => x.kind === 'svg' ? sum + svgSize(x).w : x.kind === 'text' && hugsBesideSiblings(x, p)
                    ? sum + Math.min(pInner, textWidth(x, x.chars ?? 0, unitW)) : sum, 0);
                const free = Math.max(0, pInner - gap * (perLine - 1) - basis * perLine - hugged);
                const total = line.reduce((sum, x) => sum + weightOf(x, p), 0);
                w = basis + (total > 0 ? free * weightOf(b, p) / total : 0);
            }
            widthOf.set(b.id, w);
            return w;
        }
        /** A row of items that hug their content and would run past the container's edge: it wraps, as the browser's shrink would have it fit. */
        /** A box in a CSS flex row that has no grow is as wide as its content; so is whatever sits in it. */
        const hugged = (b: Block): boolean => {
            if (b.kind === 'unit' || b.kind === 'overlay' || b.kind === 'stageContent') return false;
            const parent = blocks.get(host.find(b.id)?.parentId ?? '');
            if (!parent) return false;
            if (parent.kind === 'overlay') {
                // A mark pinned to one side is as wide as its content; a bar pinned to both sides (or a backdrop) fills.
                const e = parent.edges ?? {};
                return !parent.behind && !(e.left !== undefined && e.right !== undefined) && b.kind !== 'stage' && b.kind !== 'image';
            }
            if (b.hugSelf) return true;
            if (parent.content && b.kind !== 'text' && !weights.has(b.id) && b !== lead && (b.kind === 'stack' || b.kind === 'panel' || b.kind === 'button' || b.kind === 'chip')) return true;
            return parent.kind !== 'unit' && b.kind !== 'image' && b.kind !== 'stage' && (parent.kind === 'stack' || parent.kind === 'panel') && hugged(parent);
        };
        /** The widths of a horizontal stack's items as they hug their labels; null when one of them does not hug (the stack does not wrap). */
        const hugWidths = (b: Block, unitW: number): number[] | null => {
            const widths = (kids.get(b.id) ?? []).map(x => {
                if (x.kind === 'text') return Math.min(innerOf(b), textWidth(x, x.chars ?? 0, unitW));
                if (x.kind === 'button' || x.kind === 'chip') {
                    const label = (kids.get(x.id) ?? []).find(k => k.kind === 'text');
                    return label ? textWidth(label, label.chars ?? 0, unitW) + 2 * space(x.padding, unitW) : undefined;
                }
                return undefined;
            });
            return widths.every(w => w !== undefined) ? widths : null;
        };
        const overflows = (b: Block, unitW: number): boolean => {
            const widths = hugWidths(b, unitW);
            if (!widths || widths.length < 2) return false;
            return widths.reduce((a, w) => a + w, 0) + gapOf(b, unitW) * (widths.length - 1) > innerOf(b);
        };
        /** An svg's materials (one per colour, in the GLB's primitive order; currentColor takes the text's ink) and its fixed slot. */
        const finishSvg = (b: Block, p: Block | undefined, unitId: string): void => {
            const { w, h } = svgSize(b);
            const surface = [b, ...ancestors(b.id)].find(x => x.surface)?.surface ?? 'background';
            const lk: Look = { ...(p?.kind === 'button' || p?.kind === 'chip' ? looks.get(p.id) : undefined), ...looks.get(b.id) };
            host.behavior(b.id, 'Renderer', { materials: b.svg!.plan.pieces.map((piece, i) => {
                const c = piece.color ?? (lk.ink ? { hex: lk.ink.hex, a: Math.round(lk.ink.a * piece.alpha * 1e3) / 1e3 } : null);
                return {
                    targetMaterialIndex: i,
                    baseColorFactor: c ? colorRef(unitId, c.hex, 'ink') : surfaceText(surface),
                    ...(c && c.a >= 1 ? { emissiveFactor: lift(c.hex, 0.3) } : {}),
                    ...(c && c.a < 1 ? { opacity: c.a } : {}),
                };
            }) });
            // The file is one unit high and its bounds are the box, so the node's scale is the size; the slot is the same size, fixed, so
            // the layout does not wait on the file to know it.
            host.behavior(b.id, 'LayoutItem', { widthMode: 'Fixed', width: round6(w), heightMode: 'Fixed', height: round6(h), depthMode: 'Fixed', depth: round6(h * SVG_RELIEF), flexGrow: 0, flexShrink: 0 });
            host.find(b.id)!.scale = [round6(h), round6(h), round6(h)];
        };
        /** A button or chip that holds an icon spaces it from its label by a share of the label's size. */
        const iconGap = (b: Block, unitW: number): number | undefined => {
            const items = kids.get(b.id) ?? [];
            if ((b.kind !== 'button' && b.kind !== 'chip') || !items.some(x => x.kind === 'svg')) return undefined;
            const label = items.find(x => x.kind === 'text');
            return label ? round6(0.45 * fontCm(label, unitW) / 100) : undefined;
        };
        spacing.settle();
        for (const u of order.filter(x => x.kind === 'unit')) {
            host.behavior(u.id, 'Layout', { gap: gapOf(u, u.width!), padding: padObject(padOf(u, u.width!), padDownOf(u, u.width!)) });
        }
        for (const b of order) {
            const unitRoot = rootOf.get(b.id)!;
            const unitW = unitRoot.width!;
            const desc = host.find(b.id)!;
            const p = desc.parentId ? blocks.get(desc.parentId) : undefined;
            const bl = looks.get(b.id);
            if (b.body) {
                host.behavior(b.body, 'Bevel', {
                    cornerRadius: bl?.radius !== undefined ? Math.round(bl.radius * unitW * 1e6) / 1e6 : radius(b.radius ?? 'r2', unitW),
                    ...(bl?.pill ? { pill: true } : {}),
                });
            }
            const fill = bl?.fill;
            if (b.body && (fill || bl?.glow || bl?.translucent)) {
                const ink = bl?.glow?.hex ?? fill?.hex;
                const alpha = Math.min(fill?.a ?? 1, bl?.translucent ? 0.55 : 1);
                host.behavior(b.body, 'Renderer', {
                    ...(fill ? { materials: [{
                        targetMaterialIndex: 0, baseColorFactor: colorRef(unitRoot.id, fill.hex, 'fill'),
                        // Near-black fills read ~#222 under the viewer's light; a lift of the colour itself keeps them true (as a surface card does).
                        ...(alpha >= 1 && ink ? { emissiveFactor: lift(ink, bl?.glow ? 0.6 : 0.3) } : {}),
                    }] } : bl?.glow ? { materials: [{ targetMaterialIndex: 0, emissiveFactor: lift(bl.glow.hex, 0.6) }] } : {}),
                    ...(alpha < 1 ? { opacity: Math.round(alpha * 1e3) / 1e3 } : {}),
                });
            }
            // box-shadow: layers of relief behind the box, offset as the shadow is, one per soft step.
            if (b.body && bl?.shadows?.length) {
                const bodyZ = host.find(b.body)?.position?.[2] ?? -0.004;
                let layer = 0;
                for (const sh of bl.shadows.slice(0, 2)) {
                    const steps = sh.blur > 0 ? 2 : 1;
                    for (let k = 0; k < steps; k++, layer++) {
                        const grow = steps === 2 ? (k === 0 ? 0.6 : 1.4) : 1;
                        const id = host.background(b.id, { color: sh.color.hex, cornerRadius: (bl.radius ?? 0) * unitW, name: `${nameOf(b.id)} Shadow ${layer + 1}`, opacity: Math.round(Math.min(1, sh.color.a * (steps === 2 ? 0.45 : 0.7)) * 1e3) / 1e3 });
                        const d = host.find(id)!;
                        d.position = [Math.round(sh.x * grow / CANVAS * unitW * 1e6) / 1e6, -Math.round((sh.y * grow + sh.blur * 0.15 * k) / CANVAS * unitW * 1e6) / 1e6, bodyZ - 0.0006 * (layer + 1)];
                        if (bl.pill) host.behavior(id, 'Bevel', { pill: true });
                    }
                }
            }
            if (b.kind === 'overlay') {
                const e = b.edges ?? {};
                const m = (v: number | undefined) => v === undefined ? 0 : Math.round(v / CANVAS * unitW * 1e6) / 1e6;
                host.behavior(b.id, 'Layout', {
                    direction: 'Y', gap: 0, alignCrossB: 'End', depth: 0.01, depthMode: 'Fixed', widthMode: 'Fill', heightMode: 'Fill', positionMode: 'Absolute',
                    alignMain: e.top !== undefined ? 'Start' : e.bottom !== undefined ? 'End' : 'Center',
                    alignCross: e.left !== undefined && e.right !== undefined ? 'Stretch' : e.left !== undefined ? 'Start' : e.right !== undefined ? 'End' : 'Center',
                    padding: { left: m(e.left), right: m(e.right), top: m(e.top), bottom: m(e.bottom) },
                });
                continue;
            }
            if (b.kind === 'unit' || b.kind === 'stageContent') continue;
            const inX = p?.dir === 'X';
            const weight = inX ? Math.round(weightOf(b, p) * 1e6) / 1e6 : 1;
            const share = inX && p.wrap !== false && p.kind === 'row'
                ? { flexGrow: weight, flexShrink: 1, width: itemMinOf(p, unitW), minWidth: itemMinOf(p, unitW) }
                : inX && (p.kind === 'split' || p.kind === 'row') ? { flexGrow: weight, flexShrink: weight, width: Math.round(widthFor(b) * 1e6) / 1e6 }
                    : inX ? { flexGrow: weight, flexShrink: 1 } : {};
            const container = b.kind === 'stack' || b.kind === 'panel' || b.kind === 'row' || b.kind === 'split' || b.kind === 'stage';
            const tight = b.kind === 'button' || b.kind === 'chip';
            if (b.kind === 'svg') { finishSvg(b, p, unitRoot.id); continue; }
            if (b.kind === 'text') {
                const hugLabel = p!.kind === 'button' || p!.kind === 'chip' || p!.inline === true || hugsBesideSiblings(b, p!) || hugged(b);
                const surface = [b, ...ancestors(b.id)].find(x => x.surface)?.surface ?? 'background';
                const type = rungType(b.rung!, unitW, style);
                const parentLook = p!.kind === 'button' || p!.kind === 'chip' ? looks.get(p!.id) : undefined;
                const lk: Look = { ...parentLook, ...looks.get(b.id) };
                const bold = lk.bold ?? b.bold ?? type.bold;
                host.behavior(b.id, 'Text', {
                    fontSize: faithful && lk.fontPx !== undefined ? fontCm(b, unitW) : type.fontSize, lineHeight: lk.lineHeight ?? type.lineHeight,
                    color: lk.ink ? colorRef(unitRoot.id, lk.ink.hex, 'ink') : surfaceText(surface), fontFamily: lk.fontFamily ?? `{${type.font}Font}`,
                    ...(bold ? { fontWeight: 'bold' } : {}), ...(lk.italic ? { fontStyle: 'italic' } : {}),
                    ...(lk.letterSpacing !== undefined ? { letterSpacing: lk.letterSpacing } : {}),
                    ...(lk.ink && lk.ink.a < 1 ? { opacity: Math.round(lk.ink.a * 1e3) / 1e3 } : {}),
                    anchorX: b.textAlign, wrap: true,
                });
                host.behavior(b.id, 'LayoutItem', hugLabel
                    ? { widthMode: 'Hug', maxWidth: Math.round(innerOf(p!) * 1e6) / 1e6, heightMode: 'Hug', depthMode: 'Fixed', depth: 0.001 }
                    : { widthMode: 'Fill', ...share, heightMode: 'Hug', depthMode: 'Fixed', depth: 0.001 });
            } else if (b.kind === 'image') {
                const w = widthFor(b);
                const aspect = parseAspect(b.aspect)!;
                host.behavior(b.id, 'Image', { aspect, fit: 'cover', cornerRadius: radius('r1', unitW) });
                host.behavior(b.id, 'LayoutItem', {
                    widthMode: 'Fill', ...share, ...aspectItemSizing(w, aspect), depthMode: 'Fixed', depth: 0.001, flexShrink: 0,
                });
            } else if (container || tight) {
                if (b.fixedPx) host.find(b.id)!.keepFixed = true;
                const stageUnit = p?.kind === 'overlay' || (p?.kind === 'unit' && p.unitKind === 'stage');
                const sized = (tight && inX) || hugged(b) ? { widthMode: 'Hug', ...(b.hugSelf && !inX ? { alignSelfCross: b.hugSelf, flexGrow: 0 } : {}) } : { widthMode: 'Fill', ...share };
                const height = b.kind !== 'stage' ? { heightMode: 'Hug' }
                    : stageUnit ? { heightMode: 'Fill' } : aspectItemSizing(widthFor(b), parseAspect(b.aspect)!);
                host.behavior(b.id, 'Layout', {
                    ...sized, ...height,
                    gap: iconGap(b, unitW) ?? gapOf(b, unitW), crossGap: crossGapOf(b, unitW), padding: padObject(padOf(b, unitW), padDownOf(b, unitW)),
                    ...(bodyLevels(b) > 0 ? { depth: depthFor(b) } : {}),
                    // Wrapped lines are placed in depth by alignContentCrossB, not alignCrossB: at the front, as one line would be.
                    ...(b.kind === 'row' || b.kind === 'split' ? { wrap: b.wrap, ...(b.wrap ? { alignContentCrossB: 'End' } : {}) } : {}),
                    ...(b.kind === 'stack' && b.dir === 'X' && overflows(b, unitW) ? { wrap: true, alignContentCrossB: 'End' } : {}),
                    ...(b.fixedPx ? {
                        flexGrow: 0, flexShrink: 0,
                        ...(b.fixedPx.w !== undefined ? { widthMode: 'Fixed', width: Math.round(b.fixedPx.w / CANVAS * unitW * 1e6) / 1e6 } : {}),
                        ...(b.fixedPx.h === undefined && !b.fixedPx.hugH ? { heightMode: 'Fill' } : {}),
                        ...(b.fixedPx.h !== undefined ? { heightMode: 'Fixed', height: Math.round(b.fixedPx.h / CANVAS * unitW * 1e6) / 1e6 } : {}),
                    } : {}),
                });
            }
        }
        for (const [unitId, list] of overrides) {
            mergeProperties(unitId, {
                properties: [
                    ...list.map(o => ({ type: 'color', name: o.name, value: o.value })),
                    { type: 'string', name: 'htmlColorRoles', value: JSON.stringify(list.map(({ name, value, role, kind }) => ({ name, value, role, kind }))) },
                ],
            });
        }
        for (const [id, spec] of entrances) {
            const b = blocks.get(id);
            if (b && b.kind !== 'unit' && b.kind !== 'stageContent') enclose(b, spec, widthFor(b), rootOf.get(id)!.width!);
        }
        for (const [id, request] of motions) {
            const b = blocks.get(id);
            if (b && b.kind !== 'unit' && b.kind !== 'stageContent') applyMotion(b, request, widthFor(b), rootOf.get(id)!.width!);
        }
        return errors;
    }

    /**
     * A moving entrance. A state pose is an absolute parent-local pose, and layout writes the block's place, so the block itself cannot
     * be moved: it gets a slot (takes its place in the flow and its sizing) holding a content group (a plain container of the same
     * size, which sits at the slot's origin, so its rest pose is zero and the offset is the whole pose) holding the block. The group
     * carries the entrance states. At rest the three boxes are the block's own.
     */
    function enclose(b: Block, spec: Entrance, width: number, unitW: number): void {
        const group = groupOf(b);
        if (group === undefined) return;
        const distance = Math.round(Math.max(0.01, spec.kind === 'rise' ? Math.min(0.15 * width, 0.06 * unitW) : Math.min(0.25 * width, 0.1 * unitW)) * 1e6) / 1e6;
        const from: [number, number, number] = spec.kind === 'rise' ? [0, -distance, 0] : [spec.kind === 'slide-left' ? -distance : distance, 0, 0];
        mergeProperties(group, {
            initialState: 'away',
            states: {
                away: { values: {}, active: false, transition: { duration: 0 } },
                home: { values: {}, active: true, enterFrom: { position: from }, transition: { duration: 0.6, delay: spec.delay, easing: 'easeOut' } },
            },
        });
    }

    /** The content group of a block, made with its slot on first ask; undefined when the block has no place to be wrapped in. */
    const enterGroups = new Map<string, string | undefined>();
    function groupOf(b: Block): string | undefined {
        if (!enterGroups.has(b.id)) enterGroups.set(b.id, wrapInGroup(b));
        return enterGroups.get(b.id);
    }
    /** Properties on `id` with `patch` merged over what is there: states and declarations are added to, not replaced. */
    function mergeProperties(id: string, patch: { properties?: { name: string; type: string; value: unknown }[]; initialState?: string; states?: Record<string, unknown>; [key: string]: unknown }): void {
        const have = (host.find(id)?.behaviors?.find(x => x.type === 'Properties')?.overrides ?? {}) as { properties?: { name: string }[]; states?: Record<string, unknown> };
        const names = new Set((patch.properties ?? []).map(x => x.name));
        host.behavior(id, 'Properties', {
            ...have, ...patch,
            properties: [...(have.properties ?? []).filter(x => !names.has(x.name)), ...(patch.properties ?? [])],
            states: { ...(have.states ?? {}), ...(patch.states ?? {}) },
        });
    }

    function wrapInGroup(b: Block): string | undefined {
        const own = host.find(b.id)!;
        const bag = (own.behaviors?.find(x => x.type === 'Layout') ?? own.behaviors?.find(x => x.type === 'LayoutItem'))?.overrides ?? {};
        const parentDir = blocks.get(own.parentId!)?.dir;
        const slot = host.interpose(b.id, `${own.name} Slot`);
        const group = slot !== undefined ? host.interpose(b.id, `${own.name} Enter`) : undefined;
        if (slot === undefined || group === undefined) return undefined;
        const base = {
            direction: 'Y', alignMain: 'Start', alignCross: 'Stretch', alignCrossB: 'End', padding: 0, gap: 0, crossGap: 0,
            depthMode: bag.depthMode ?? 'Fixed', depth: bag.depth ?? 0.01,
            // A fixed height belongs to the block; the groups around it hug it.
            heightMode: bag.heightMode === 'Fixed' || bag.heightMode === undefined ? 'Hug' : bag.heightMode,
        };
        const keep = (...names: string[]) => Object.fromEntries(names.filter(n => bag[n] !== undefined).map(n => [n, bag[n]]));
        host.behavior(slot, 'Layout', {
            ...base, widthMode: bag.widthMode ?? 'Fill',
            ...keep('width', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'flexGrow', 'flexShrink', 'alignSelfCross', 'alignSelfCrossB'),
        });
        // What the block asked of its siblings now belongs to the slot; inside it the block only fills.
        for (const name of ['width', 'minWidth', 'flexGrow', 'flexShrink']) delete bag[name];
        // A container beside siblings in a row is stretched to the row's height; the stretch passes through the groups to the block as spare height they grow into.
        const stretched = own.behaviors?.some(x => x.type === 'Layout') && bag.heightMode === 'Hug' && parentDir === 'X';
        host.behavior(group, 'Layout', { ...base, ...(stretched ? { flexGrow: 1, flexShrink: 1 } : {}), widthMode: bag.widthMode ?? 'Fill', ...keep('maxWidth', 'minHeight', 'maxHeight') });
        if (stretched) Object.assign(bag, { flexGrow: 1, flexShrink: 1 });
        return group;
    }

    /** The CSS px a unit's width stands for: type, space and radius are shares of the same canvas. */
    const CANVAS_PX = 1280;
    const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

    /** Every object in a block's subtree, the block's own first. */
    function subtreeOf(id: string): SpatialObjectDescriptor[] {
        const all = host.objects();
        const out: SpatialObjectDescriptor[] = [];
        const walk = (at: string) => {
            const own = all.find(x => x.id === at);
            if (own) out.push(own);
            for (const kid of all.filter(x => x.parentId === at)) walk(kid.id);
        };
        walk(id);
        return out;
    }

    /** Bind the opacity of everything in a block to an expression: text through its own opacity, surfaces through their renderer's. */
    function bindOpacity(blockId: string, expression: string): void {
        const inner = expression.slice(1, -1);
        let skipped = false;
        for (const d of subtreeOf(blockId)) {
            const has = (type: string) => d.behaviors?.find(x => x.type === type);
            if (has('Text')) host.behavior(d.id, 'Text', { ...(has('Text')!.overrides ?? {}), opacity: expression });
            else if (has('Renderer')) {
                const was = has('Renderer')!.overrides ?? {};
                const own = typeof was.opacity === 'number' && was.opacity !== 1 ? ` * ${was.opacity}` : '';
                host.behavior(d.id, 'Renderer', { ...was, opacity: own ? `{(${inner})${own}}` : expression });
            } else if (has('Image')) skipped = true;
        }
        if (skipped) host.warn?.(`html(): opacity on "${nameOf(blockId)}" does not reach the pictures inside it; they stay opaque.`);
    }

    /** CSS motion on a block: a clip, an opacity animation and hover / press states, all on the block's content group. */
    function applyMotion(b: Block, request: MotionRequest, width: number, unitW: number): void {
        const warn = (message: string) => host.warn?.(`html(): ${message}`);
        const group = groupOf(b);
        if (group === undefined) { warn(`motion on "${nameOf(b.id)}" needs a block inside a unit; skipped.`); return; }
        const m = { metresPerPx: unitW / CANVAS_PX, width };
        const groupData = host.find(group)!;

        if (request.clip) {
            const { anim, frames } = request.clip;
            const planned = planClip(anim, frames, m);
            if (planned) {
                const plan = planned;
                if (plan.seam) warn(`animation "${anim.name}" on "${nameOf(b.id)}" loops but ends on a different pose than it starts on, so it jumps at the end of each cycle (the CSS does the same).`);
                // A channel that never leaves the block's own pose is left out of the clip.
                const flat = (pick: (k: typeof plan.keys[number]) => number[] | undefined, rest: number[], sign = false) => plan.keys.every(k => pick(k)!.every((v, i) => Math.abs((sign ? Math.abs(v) : v) - (sign ? Math.abs(rest[i]) : rest[i])) < 1e-6));
                const drop = { position: flat(k => k.position, [0, 0, 0]), rotation: flat(k => k.rotation, [0, 0, 0, 1], true), scale: flat(k => k.scale, [1, 1, 1]) };
                const keyed = (keys: typeof plan.keys) => keys.map(k => ({
                    time: k.time,
                    ...(drop.position ? {} : { position: k.position }), ...(drop.rotation ? {} : { rotation: k.rotation }), ...(drop.scale ? {} : { scale: k.scale }),
                }));
                if (drop.position && drop.rotation && drop.scale) {
                    warn(`animation "${anim.name}" on "${nameOf(b.id)}" never moves, turns or scales the block; skipped.`);
                } else {
                    const name = `css-${anim.name}`.slice(0, 40);
                    const clips = [{ name, keyframes: keyed(plan.keys) }];
                    const states: Record<string, unknown> = {};
                    const src = () => `/generated/clip-${hash(JSON.stringify({ clips, fps: plan.fps }))}.glb`;
                    if (plan.loop && plan.hold > 0) {
                        // The loop waits its delay before the first pass: a held pose is an entrance clip, and the loop follows it.
                        const at = anim.fill === 'backwards' || anim.fill === 'both' ? plan.keys[0] : { position: bakePose(REST, m).position, rotation: bakePose(REST, m).rotation, scale: bakePose(REST, m).scale };
                        clips.push({ name: 'hold', keyframes: keyed([{ time: 0, ...at }, { time: round6(plan.hold), ...at }]) });
                    }
                    const file = src();
                    states.main = plan.loop ? { src: file, clip: name } : undefined;
                    if (!plan.loop) states.enter = { src: file, clip: name, ...(plan.hold > 0 ? { delay: round6(plan.hold) } : {}) };
                    else if (plan.hold > 0) states.enter = { src: file, clip: 'hold' };
                    for (const key of Object.keys(states)) if (states[key] === undefined) delete states[key];
                    groupData.clips = [...(groupData.clips ?? []), { src: file, clips, fps: plan.fps }];
                    host.behavior(group, 'AnimationSequence', { states });
                }
            }
        }

        const declare = (name: string, value: number) => mergeProperties(group, { properties: [{ name, type: 'number', value }] });
        const run = request.fade;
        if (run && !Number.isFinite(run.anim.iterations)) {
            const cycle = opacityCycle(run.anim, run.frames, 'motionPhase');
            if (cycle) {
                declare('motionPhase', startPhase(run.anim));
                bindOpacity(b.id, cycle.expression);
                groupData.clocks = [...(groupData.clocks ?? []), { property: 'motionPhase', period: 1, rateScale: round6(1 / cycleOf(run.anim)) }];
            }
        } else if (run) {
            const ends = opacityRun(run.anim, run.frames);
            if (ends) {
                if (ends.stops > 2) warn(`opacity animation "${run.anim.name}" on "${nameOf(b.id)}" has ${ends.stops} stops; a one-shot fade runs from the first to the last.`);
                if (run.anim.fill !== 'forwards' && run.anim.fill !== 'both' && ends.to !== 1) warn(`opacity animation "${run.anim.name}" on "${nameOf(b.id)}" holds its last opacity; CSS would return to 1 (use animation-fill-mode: forwards to say so).`);
                declare('motionOpacity', ends.to);
                bindOpacity(b.id, '{motionOpacity}');
                const have = (host.find(group)!.behaviors?.find(x => x.type === 'Properties')?.overrides ?? {}) as { states?: Record<string, { values?: Record<string, unknown>; transition?: unknown }> };
                const into = (name: string, value: number, transition: Record<string, unknown>) => ({
                    ...(have.states?.[name] ?? {}), values: { ...(have.states?.[name]?.values ?? {}), motionOpacity: value },
                    ...(have.states?.[name]?.transition ? {} : { transition }),
                });
                mergeProperties(group, {
                    initialState: 'away',
                    states: {
                        away: into('away', ends.from, { duration: 0 }),
                        home: into('home', ends.to, { duration: run.anim.duration * run.anim.iterations, delay: Math.max(0, run.anim.delay), easing: canopyEasing(run.anim.timing) }),
                    },
                });
            }
        }

        const states: Record<string, unknown> = {};
        for (const [name, ask] of [[':hover', request.hover], [':active', request.active]] as const) {
            if (!ask) continue;
            const t = request.transition;
            const state: Record<string, unknown> = { values: {}, transition: t ? { duration: t.duration, ...(t.delay ? { delay: t.delay } : {}), easing: canopyEasing(t.timing) } : { duration: 0 } };
            if (ask.pose) {
                const baked = bakePose(ask.pose, m);
                const near = (a: number[], b: number[]) => a.every((v, i) => Math.abs(v - b[i]) < 1e-6);
                state.transform = {
                    ...(near(baked.position, [0, 0, 0]) ? {} : { position: baked.position.map(round6) }),
                    ...(near(ask.pose.r, [0, 0, 0]) ? {} : { rotation: baked.rotation.map(round6) }),
                    ...(near(baked.scale, [1, 1, 1]) ? {} : { scale: baked.scale.map(round6) }),
                };
            }
            if (ask.opacity !== undefined) {
                if (run && !Number.isFinite(run.anim.iterations)) {
                    // As in CSS, a running animation wins over the :hover/:active declaration: the loop keeps the opacity.
                    warn(`${name} opacity on "${nameOf(b.id)}" has no effect while its infinite opacity animation runs; skipped.`);
                } else {
                    // A finite fade already declared and bound motionOpacity (to its end value); otherwise this block starts at 1.
                    if (!run) { declare('motionOpacity', 1); bindOpacity(b.id, '{motionOpacity}'); }
                    (state.values as Record<string, unknown>).motionOpacity = ask.opacity;
                }
            }
            states[name] = state;
        }
        if (Object.keys(states).length) {
            mergeProperties(group, { states });
            // Pointer events reach the object that owns the picked mesh; the group owns none, so it gets a collider that fits its content.
            if (!groupData.behaviors?.some(x => x.type === 'BoxCollider')) host.behavior(group, 'BoxCollider', {});
        }
    }

    const unitOf = (id: string): string | undefined => {
        for (let at: string | undefined = id; at; at = host.find(at)?.parentId) if (blocks.get(at)?.kind === 'unit') return at;
        return undefined;
    };
    /** `html(markup, opts)`: restricted HTML and flex CSS compiled to the blocks above; see html-blocks.ts. */
    function html(markup: unknown, opts?: HtmlOptions) {
        const kit: HtmlKit = {
            unit, stack, panel, row, split, text, image, figure, facts, stage3d,
            button: (l, o) => labelled('button', l, o), chip: (l, o) => labelled('chip', l, o),
            svg, unitOf, units: () => order.filter(b => b.kind === 'unit').map(b => b.id),
            find: id => host.find(id), behavior: (id, type, o) => host.behavior(id, type, o),
            interaction: id => host.interaction?.(id),
            enclose: (id, kind, delay) => {
                if (!blocks.has(id) || blocks.get(id)!.kind === 'unit' || !host.find(id)?.parentId) return false;
                entrances.set(id, { kind, delay });
                return true;
            },
            fonts: options.fonts,
            look: (id, l) => { if (blocks.has(id)) looks.set(id, { ...looks.get(id), ...l }); },
            overlay,
            hug: (id, side) => { const b = blocks.get(id); if (b) b.hugSelf = side; },
            fixed: (id, size) => { const b = blocks.get(id); if (b) b.fixedPx = size; },
            faithful: () => { faithful = true; },
            weigh: (id, weight) => { if (blocks.has(id)) weights.set(id, weight); },
            motion: (id, request) => {
                if (!blocks.has(id) || blocks.get(id)!.kind === 'unit' || !host.find(id)?.parentId) return false;
                motions.set(id, { ...motions.get(id), ...request });
                return true;
            },
            warn: m => host.warn?.(m),
        };
        return compileHtml(markup, opts, kit);
    }

    const api: Record<string, unknown> = { html, color, unit, stack, panel, row, split, text, image, figure, button: (l: unknown, o?: Opts) => labelled('button', l, o), chip: (l: unknown, o?: Opts) => labelled('chip', l, o), facts, stage3d };
    return { api, finalize, notes: () => [...notesOut] };
}
